import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  notification_date: string;
}

interface NotificationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

async function fetchNotifications() {
  // credentials: "include" is automatically injected by the global fetch interceptor
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/notification/list?page=0&size=3`,
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  const data = await response.json();
  if (data.api.response !== "FETCHED") {
    throw new Error(data.message);
  }

  return data.result.content;
}

async function markAllAsRead() {
  // credentials: "include" is automatically injected by the global fetch interceptor
  const response = await fetch(
    `${import.meta.env.VITE_API_URL}/notification/mark-all-as-read`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  const data = await response.json();
  if (data.api.response !== "SUCCESS") {
    throw new Error(data.message);
  }

  return data;
}

export function NotificationsModal({
  open,
  onOpenChange,
}: NotificationsModalProps) {
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: fetchNotifications,
    enabled: open,
  });

  const { mutate: handleMarkAllAsRead } = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Notifications</DialogTitle>
        </DialogHeader>

        <div className="flex justify-between gap-4 mt-4">
          <Button
            variant="secondary"
            className="w-full font-medium"
            disabled={notifications.length === 0}
            asChild
          >
            <Link to="/notifications">View All</Link>
          </Button>
          <Button
            variant="secondary"
            className="w-full font-medium"
            onClick={() => handleMarkAllAsRead()}
            disabled={notifications.length === 0}
          >
            Mark all as read
          </Button>
        </div>

        <div className="mt-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              You don't have any pending notifications.
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.map((notification: Notification) => (
                <div key={notification.id} className="flex items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <img src="/logo-two.png" alt="" className="h-8 w-8" />
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center gap-1.5">
                            <div
                              className={cn(
                                "h-1.5 w-1.5 rounded-full",
                                notification.is_read
                                  ? "bg-muted"
                                  : "bg-emerald-600"
                              )}
                            />
                            <span className="text-sm text-muted-foreground">
                              {notification.type}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(
                              new Date(notification.notification_date),
                              "PPp"
                            )}
                          </span>
                        </div>

                        <div className="border rounded-lg p-3">
                          <div className="flex justify-between items-center">
                            <p className="font-medium">{notification.title}</p>
                            {!notification.is_read && (
                              <div className="bg-emerald-50 px-2 py-0.5 rounded flex items-center gap-1">
                                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                                <span className="text-xs text-emerald-600">
                                  New
                                </span>
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {notification.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
